import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraFormField, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { proposalReviewClient } from '../../../../lib/connect/clients';
import { NAME_CHIPS, outcomeLabel } from './reviewState';
import { reviewErrorMessage, reviewKeys, useReviewList, useReviewSections } from './useProposalReviews';
const today = ()=>new Date().toLocaleDateString('en-CA');
/** Why the create form can't be submitted yet, or '' when it can. */ export function createBlockedReason(f) {
    if (!f.name.trim()) return 'Enter a review name.';
    if (f.scope.length === 0) return 'Select at least one section to review.';
    if (!f.startsOn) return 'Choose a start date.';
    if (f.dueOn && f.dueOn < f.startsOn) return 'The due date is before the start date.';
    return '';
}
export function CreateReviewForm({ projectId, onCreated }) {
    const queryClient = useQueryClient();
    const { sections } = useReviewSections(projectId);
    const [name, setName] = useState('');
    const [startsOn, setStartsOn] = useState(today());
    const [dueOn, setDueOn] = useState('');
    const [reviewers, setReviewers] = useState('');
    const [scope, setScope] = useState([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const blocked = createBlockedReason({
        name,
        startsOn,
        dueOn,
        scope
    });
    const submit = async (e)=>{
        e.preventDefault();
        if (blocked || inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        setError('');
        try {
            const resp = await proposalReviewClient.createProposalReview({
                projectId,
                fields: {
                    name: name.trim(),
                    startsOn,
                    dueOn,
                    reviewers: reviewers.split(',').map((r)=>r.trim()).filter(Boolean),
                    scopeSectionIds: scope
                }
            });
            await queryClient.invalidateQueries({
                queryKey: reviewKeys.list(projectId)
            });
            if (resp.review) onCreated(resp.review.id);
        } catch (err) {
            setError(reviewErrorMessage(err));
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    };
    const toggle = (id)=>setScope((s)=>s.includes(id) ? s.filter((x)=>x !== id) : [
                ...s,
                id
            ]);
    return /*#__PURE__*/ _jsxs("form", {
        onSubmit: submit,
        className: "space-y-3 rounded-lg border border-border bg-card p-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "data-testid": Testids.proposalReviews.nameInput,
                        "aria-label": "Review name",
                        placeholder: "Review name, e.g. Pink Team",
                        value: name,
                        onChange: (e)=>setName(e.target.value)
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex gap-1.5",
                        children: NAME_CHIPS.map((chip)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                variant: "outline",
                                size: "sm",
                                "data-testid": Testids.proposalReviews.nameChip,
                                onClick: ()=>setName(`${chip} Team`),
                                children: chip
                            }, chip))
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Start date",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Start date",
                            type: "date",
                            "data-testid": Testids.proposalReviews.startDate,
                            value: startsOn,
                            onChange: (e)=>setStartsOn(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Due date",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Due date",
                            type: "date",
                            "data-testid": Testids.proposalReviews.dueDate,
                            value: dueOn,
                            onChange: (e)=>setDueOn(e.target.value)
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                "data-testid": Testids.proposalReviews.reviewerInput,
                "aria-label": "Reviewers",
                placeholder: "Reviewers, comma separated",
                value: reviewers,
                onChange: (e)=>setReviewers(e.target.value)
            }),
            /*#__PURE__*/ _jsxs("fieldset", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("legend", {
                        className: "text-sm font-medium text-foreground",
                        children: "Sections in scope"
                    }),
                    sections.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "This proposal has no sections yet."
                    }),
                    sections.map((s)=>/*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                label: `${s.volumeName} — ${s.title}`,
                                "data-testid": Testids.proposalReviews.scopeCheckbox,
                                controlProps: {
                                    checked: scope.includes(s.sectionId),
                                    onCheckedChange: ()=>toggle(s.sectionId)
                                }
                            })
                        }, s.sectionId))
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.proposalReviews.error,
                className: "text-sm text-danger",
                children: error
            }),
            blocked && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: blocked
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                type: "submit",
                "data-testid": Testids.proposalReviews.submitCreate,
                disabled: !!blocked || busy,
                children: busy ? 'Creating…' : 'Create review'
            })
        ]
    });
}
export function ProposalReviewsPage() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const list = useReviewList(id);
    const [creating, setCreating] = useState(false);
    const reviews = list.data ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 pt-4 sm:px-6",
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "text-lg font-semibold text-foreground",
                                children: "Reviews"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Review specific versions of your sections."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.proposalReviews.createButton,
                        onClick: ()=>setCreating((c)=>!c),
                        children: creating ? 'Cancel' : 'Create review'
                    })
                ]
            }),
            creating && /*#__PURE__*/ _jsx(CreateReviewForm, {
                projectId: id,
                onCreated: (reviewId)=>navigate(`/proposal/${id}/v2/reviews/${reviewId}`)
            }),
            list.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.proposalReviews.error,
                className: "text-sm text-danger",
                children: reviewErrorMessage(list.error)
            }),
            list.isSuccess && reviews.length === 0 && !creating && /*#__PURE__*/ _jsx("p", {
                className: "rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground",
                children: "No reviews yet. Create one to review specific versions of your sections."
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: reviews.map((r)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.proposalReviews.reviewRow,
                        className: "rounded-lg border border-border bg-card p-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: `/proposal/${id}/v2/reviews/${r.id}`,
                                        className: "font-medium text-foreground hover:underline",
                                        children: r.name
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex gap-1.5",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: r.status === 'open' ? 'info' : 'neutral',
                                                children: r.status === 'open' ? 'Open' : 'Closed'
                                            }),
                                            r.outcome && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: "outline",
                                                children: outcomeLabel(r.outcome)
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mt-1 text-sm text-muted-foreground",
                                children: [
                                    "Starts ",
                                    r.startsOn,
                                    r.dueOn ? ` · Due ${r.dueOn}` : '',
                                    " · ",
                                    r.materialCount,
                                    " material",
                                    r.materialCount === 1 ? '' : 's',
                                    " · ",
                                    r.findingCount,
                                    " finding",
                                    r.findingCount === 1 ? '' : 's'
                                ]
                            })
                        ]
                    }, r.id))
            })
        ]
    });
}
